import { useMemo } from "react";
import { create } from "zustand";

import type { Deployment } from "../../api/types";
import { designate } from "../../components/attention";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { useDeployment } from "../units/useDeployment";
import { orientDeployment } from "./orientDeployment";
import { delta, fitFor, orientationModel, snapToTemplate, transformOf } from "./orientation";

/** Mode « Orienter la manœuvre » (docs/14 §2.14) : état partagé entre le panneau Manœuvre (rose, champ, Appliquer),
 *  la carte (anneau, flèche, poignée, clavier) et les calques (aperçu). Non persistant : quitter le mode, changer de
 *  vue, de dossier ou de base du dispositif le referme. */
interface OrientationState {
  /** Mode ouvert (carte d'orientation dans le panneau, anneau sur la carte). */
  open: boolean;
  /** Azimut visé, en degrés du nord vrai (null mode fermé). */
  target: number | null;
  /** Faire tourner aussi les graphiques tactiques (cochée par défaut). */
  graphics: boolean;
  /** Écriture en cours (« Calage sur le terrain… ») : l'aperçu reste affiché jusqu'à la réponse (B28). */
  pending: boolean;
  /** Version du dossier à l'ouverture : l'aperçu ne s'applique qu'à ce déploiement (la réponse du serveur, d'une
   *  autre version, s'affiche telle quelle). */
  version: number | null;
  /** Demande de clignotement d'un élément du panneau (rose, Callout) : compteur incrémenté, lu par le panneau. */
  flash: { target: "rose" | "callout" | "apply"; at: number } | null;
  start: (deployment: Deployment) => boolean;
  setTarget: (deg: number) => void;
  setGraphics: (on: boolean) => void;
  setPending: (on: boolean) => void;
  /** Le dossier a changé pendant le mode sans écriture d'orientation (autre onglet, fiche restée ouverte) : l'aperçu
   *  repart du nouveau déploiement, le sens visé est gardé. */
  rebase: (version: number) => void;
  requestFlash: (target: "rose" | "callout" | "apply") => void;
  /** Annule (Échap, « Annuler ») : le mode se ferme sans rien écrire. */
  cancel: () => void;
  close: () => void;
}

const CLOSED = { open: false, target: null, pending: false, version: null, flash: null } as const;

/** Veille des fiches pendant le mode (désabonnement), posée par `start`, levée par `cancel` et `close`. */
let unwatchCards: (() => void) | null = null;

/** Fiche d'objet (unité, LIMA, ligne de la ZO, graphique…) : ses écritures partiraient d'un autre état que l'aperçu.
 *  Celle qui était ouverte se ferme à l'ouverture du mode ; en ouvrir une pendant le mode est refusé, avec l'avis et
 *  « Appliquer » désigné. */
function watchCards(): void {
  unwatchCards?.();
  if (useUi.getState().selection) useUi.getState().select(null);
  unwatchCards = useUi.subscribe((state, previous) => {
    if (!state.selection || state.selection === previous.selection || !useOrientation.getState().open) return;
    useUi.getState().select(null);
    refuseWhileOrienting();
  });
}

function unwatch(): void {
  unwatchCards?.();
  unwatchCards = null;
}

export const useOrientation = create<OrientationState>()((set) => ({
  ...CLOSED,
  graphics: true,
  start: (deployment) => {
    const model = orientationModel(deployment.corridor?.orientation);
    if (!model || deployment.exercise_version == null) return false;
    set({ open: true, target: model.current.bearing, pending: false, version: deployment.exercise_version, flash: null });
    watchCards();
    return true;
  },
  setTarget: (deg) => set({ target: deg }),
  setGraphics: (graphics) => set({ graphics }),
  setPending: (pending) => set({ pending }),
  rebase: (version) => set({ version }),
  requestFlash: (target) => set({ flash: { target, at: Date.now() } }),
  cancel: () => {
    unwatch();
    set({ ...CLOSED });
  },
  close: () => {
    unwatch();
    set({ ...CLOSED });
  },
}));

/** Écriture du dispositif demandée pendant le mode orientation (bouton du panneau, fiche d'objet, fiche d'une ligne
 *  de la ZO, « Modifier le fuseau ») : refusée, avec l'avis et « Appliquer » désigné ; rend vrai si elle l'est. */
export function refuseWhileOrienting(): boolean {
  if (!useOrientation.getState().open) return false;
  useUi.getState().notify({ text: t("Appliquez ou annulez d'abord le sens de la manœuvre"), tone: "info" });
  // Cible `ORIENTATION_ATTENTION.apply` (useApplyOrientation, qui importe ce module).
  designate('[data-attention="orientation-apply"]', "auto");
  return true;
}

/** Déploiement montré par les calques de la carte : celui du serveur, ou, mode orientation ouvert, son aperçu dans le
 *  sens visé (`orientDeployment`). Le reste de l'application garde `useDeployment`. */
export function useShownDeployment(): ReturnType<typeof useDeployment> {
  const query = useDeployment();
  const open = useOrientation((s) => s.open);
  const target = useOrientation((s) => s.target);
  const graphics = useOrientation((s) => s.graphics);
  const version = useOrientation((s) => s.version);
  const data = query.data;
  const shown = useMemo(() => previewOf(data, open, target, graphics, version), [data, open, target, graphics, version]);
  return shown === data ? query : ({ ...query, data: shown } as ReturnType<typeof useDeployment>);
}

/** Aperçu d'un déploiement dans le sens visé (fonction pure) ; le déploiement tel quel hors du mode, pour une autre
 *  version du dossier, ou sans état d'orientation. */
export function previewOf(
  data: Deployment | undefined,
  open: boolean,
  target: number | null,
  graphics: boolean,
  version: number | null,
): Deployment | undefined {
  if (!data || !open || target === null || data.exercise_version !== version) return data;
  const model = orientationModel(data.corridor?.orientation);
  if (!model) return data;
  const dst = fitFor(model, snapToTemplate(target, model.template));
  const step = delta(dst) - delta(model.current);
  if (step === 0 && dst.shift[0] === model.current.shift[0] && dst.shift[1] === model.current.shift[1]) return data;
  return orientDeployment(data, transformOf(model.current, dst), step, { graphics });
}
